import { create } from "zustand";
import { flushPendingEdits } from "@/lib/email-builder/pending-edits";
import {
  clearTempIds,
  newTempId,
  preorderIds,
  registerTempId,
  renameBlockIds,
  resolveBlockId,
  resolveOptionalBlockId,
} from "@/lib/email-builder/temp-ids";
import { immer } from "zustand/middleware/immer";
import type { EmailDocument, EmailBlock, DocumentConfiguration } from "@/types/email";
import { emailBuilderService } from "@/lib/api-services/email-builder-service";
import {
  buildBlockTree,
  moveBlock as moveBlockInTree,
  deleteBlock as deleteBlockFromTree,
  duplicateBlockTree,
  findBlockById,
} from "@/lib/email-builder/block-tree-utils";
import {
  emptyHistory,
  recordEdit,
  renameInHistory,
  renameKeys,
  type EditHistory,
} from "@/lib/email-builder/edit-history";

interface PendingOperation {
  id: string;
  type: "add" | "update" | "move" | "delete" | "duplicate";
  promise: Promise<any>;
}

interface EmailBuilderState {
  // Document state
  document: EmailDocument | null;
  blockTree: EmailBlock[];

  // Selection and UI
  selectedBlockId: string | null;
  hoveredBlockId: string | null;

  // Editor state
  isDirty: boolean;
  isSaving: boolean;
  lastSaved: Date | null;
  saveError: string | null;

  // Loading states
  isLoading: boolean;
  error: string | null;

  // Optimistic updates
  pendingOperations: PendingOperation[];

  // Undo/redo of configuration edits (see edit-history.ts)
  history: EditHistory;
  /** Last configuration JSON sent to (or loaded from) the server, per block */
  persistedConfigs: Record<string, string>;

  // Document actions
  loadDocument: (id: string) => Promise<void>;
  createDocument: (name: string) => Promise<EmailDocument>;
  createFromTemplate: (templateId: string, name: string) => Promise<EmailDocument>;
  saveDocument: () => Promise<void>;
  updateDocumentMetadata: (data: {
    name?: string;
    status?: "Draft" | "Published" | "Archived";
  }) => Promise<void>;
  updateDocumentConfigLocal: (config: DocumentConfiguration) => void;
  updateDocumentConfig: (config: DocumentConfiguration) => Promise<void>;
  deleteDocument: (id: string) => Promise<void>;

  // Block actions
  addBlock: (
    blockType: string,
    parentBlockId: string | undefined,
    orderIndex: number,
    configuration?: any
  ) => Promise<EmailBlock>;
  updateBlockConfigLocal: (blockId: string, configuration: any) => void;
  /** Merges `patch` into the block's latest local configuration (no API call). */
  patchBlockConfigLocal: (blockId: string, patch: Record<string, unknown>) => void;
  /** Sends the block's latest local configuration to the server. */
  persistBlockConfig: (blockId: string) => Promise<void>;
  updateBlockConfig: (
    blockId: string,
    configuration: any,
    options?: { recordHistory?: boolean }
  ) => Promise<void>;
  moveBlockAction: (
    blockId: string,
    parentBlockId: string | undefined,
    orderIndex: number
  ) => Promise<void>;
  deleteBlockAction: (blockId: string) => Promise<void>;
  duplicateBlockAction: (
    blockId: string,
    targetParentId?: string
  ) => Promise<void>;

  // Selection actions
  selectBlock: (blockId: string | null) => void;
  hoverBlock: (blockId: string | null) => void;

  // Undo/redo actions (configuration edits only)
  undo: () => Promise<void>;
  redo: () => Promise<void>;
  canUndo: () => boolean;
  canRedo: () => boolean;

  // Version actions
  /** Automatic checkpoints are pruned by the server (only the newest are kept). */
  createCheckpoint: (description?: string, changeType?: "Manual" | "AutoSave") => Promise<void>;
  restoreVersion: (versionNumber: number) => Promise<void>;
  getVersionHistory: () => Promise<void>;

  /** Records a failed block request and reloads the tree from the server. */
  handleOperationFailure: (operationId: string, message: string) => void;
  /** Replaces the local block tree with the server's, once in-flight requests have settled. */
  resyncBlockTree: () => Promise<void>;

  // Utility actions
  /** Sends debounced edits immediately and resolves once no block request is in flight. */
  flushAndWaitForPendingOperations: () => Promise<void>;
  reset: () => void;
  setError: (error: string | null) => void;
}

const initialState = {
  document: null,
  blockTree: [],
  selectedBlockId: null,
  hoveredBlockId: null,
  isDirty: false,
  isSaving: false,
  lastSaved: null,
  saveError: null,
  isLoading: false,
  error: null,
  pendingOperations: [],
  history: emptyHistory(),
  persistedConfigs: {} as Record<string, string>,
};

export const useEmailBuilderStore = create<EmailBuilderState>()(
  immer((set, get) => ({
    ...initialState,

    // ===== DOCUMENT ACTIONS =====

    loadDocument: async (id: string) => {
      set((state) => {
        state.isLoading = true;
        state.error = null;
      });

      try {
        const document = await emailBuilderService.getDocument(id);
        const tree = buildBlockTree(document.blocks || []);

        set((state) => {
          state.document = document;
          state.blockTree = tree;
          state.isLoading = false;
          state.isDirty = false;
          state.lastSaved = document.updatedAt
            ? new Date(document.updatedAt)
            : null;
          state.selectedBlockId = null;
          state.hoveredBlockId = null;
          state.history = emptyHistory();
          state.persistedConfigs = collectConfigs(tree);
        });
      } catch (error: any) {
        set((state) => {
          state.isLoading = false;
          state.error = error.message || "Failed to load document";
        });
        throw error;
      }
    },

    createDocument: async (name: string) => {
      set((state) => {
        state.isLoading = true;
        state.error = null;
      });

      try {
        const document = await emailBuilderService.createDocument({
          name,
        });
        const tree = buildBlockTree(document.blocks || []);

        set((state) => {
          state.document = document;
          state.blockTree = tree;
          state.isLoading = false;
          state.isDirty = false;
          state.lastSaved = new Date();
          state.selectedBlockId = null;
          state.hoveredBlockId = null;
          state.history = emptyHistory();
          state.persistedConfigs = collectConfigs(tree);
        });

        return document;
      } catch (error: any) {
        set((state) => {
          state.isLoading = false;
          state.error = error.message || "Failed to create document";
        });
        throw error;
      }
    },

    createFromTemplate: async (templateId: string, name: string) => {
      set((state) => {
        state.isLoading = true;
        state.error = null;
      });

      try {
        const document = await emailBuilderService.createFromTemplate(
          templateId,
          name
        );
        const tree = buildBlockTree(document.blocks || []);

        set((state) => {
          state.document = document;
          state.blockTree = tree;
          state.isLoading = false;
          state.isDirty = false;
          state.lastSaved = new Date();
          state.selectedBlockId = null;
          state.hoveredBlockId = null;
          state.history = emptyHistory();
          state.persistedConfigs = collectConfigs(tree);
        });

        return document;
      } catch (error: any) {
        set((state) => {
          state.isLoading = false;
          state.error = error.message || "Failed to create from template";
        });
        throw error;
      }
    },

    saveDocument: async () => {
      const { document, isSaving } = get();
      if (!document || isSaving) return;

      set((state) => {
        state.isSaving = true;
        state.saveError = null;
      });

      try {
        // Blocks are persisted by their own API calls; saving means making sure none are still
        // waiting: send debounced edits now, then wait for every in-flight request.
        await get().flushAndWaitForPendingOperations();

        // Reload document metadata from the backend
        const updatedDoc = await emailBuilderService.getDocument(document.id);
        // The editor may have been left (store reset) or another document opened meanwhile
        if (get().document?.id !== document.id) return;

        set((state) => {
          state.document = updatedDoc;
          state.isSaving = false;
          state.isDirty = false;
          state.lastSaved = new Date();
          state.saveError = null;
        });
      } catch (error: any) {
        const errorMessage = error.message || "Failed to save document";
        set((state) => {
          state.isSaving = false;
          state.saveError = errorMessage;
        });
        // Log for debugging but don't throw to allow retry
        console.error("Save failed:", errorMessage, error);
        throw error;
      }
    },

    updateDocumentMetadata: async (data) => {
      const { document } = get();
      if (!document) return;

      try {
        const updated = await emailBuilderService.updateDocument(
          document.id,
          data
        );
        if (get().document?.id !== document.id) return;

        set((state) => {
          // Keep the local configuration: it may hold edits newer than this request
          state.document = { ...updated, configuration: state.document?.configuration ?? updated.configuration };
          state.lastSaved = new Date();
        });
      } catch (error: any) {
        set((state) => {
          state.error = error.message || "Failed to update document";
        });
        throw error;
      }
    },

    updateDocumentConfigLocal: (config: DocumentConfiguration) => {
      const { document } = get();
      if (!document) return;

      set((state) => {
        if (state.document) {
          state.document.configuration = JSON.stringify(config);
          state.isDirty = true;
        }
      });
    },

    updateDocumentConfig: async (config: DocumentConfiguration) => {
      const { document } = get();
      if (!document) return;

      try {
        const updated = await emailBuilderService.updateDocument(
          document.id,
          { configuration: JSON.stringify(config) }
        );
        if (get().document?.id !== document.id) return;

        set((state) => {
          // Keep the local configuration: settings changed while this request was in flight
          // would otherwise snap back to the older value being typed over
          state.document = { ...updated, configuration: state.document?.configuration ?? updated.configuration };
          state.lastSaved = new Date();
        });
      } catch (error: any) {
        console.error('Failed to update document configuration:', error);
        set((state) => {
          state.error = error.message || "Failed to update document configuration";
        });
        throw error;
      }
    },

    deleteDocument: async (id: string) => {
      try {
        await emailBuilderService.deleteDocument(id);
        set((state) => {
          Object.assign(state, initialState);
        });
      } catch (error: any) {
        set((state) => {
          state.error = error.message || "Failed to delete document";
        });
        throw error;
      }
    },

    // ===== BLOCK ACTIONS WITH OPTIMISTIC UPDATES =====

    addBlock: async (
      blockType: string,
      parentBlockId: string | undefined,
      orderIndex: number,
      configuration?: any
    ) => {
      const { document, blockTree } = get();
      if (!document) throw new Error("No document loaded");

      // Temporary ID until the server responds; requests targeting it wait for the real one
      const tempId = newTempId();

      // Create optimistic block
      const optimisticBlock: EmailBlock = {
        id: tempId,
        emailDocumentId: document.id,
        blockType,
        configuration: configuration ? JSON.stringify(configuration) : "{}",
        parentBlockId,
        childBlocks: [],
        orderIndex,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      // Optimistically update tree
      const newTree = addBlockToTree(blockTree, optimisticBlock, parentBlockId);
      set((state) => {
        state.blockTree = newTree;
        state.isDirty = true;
        state.selectedBlockId = tempId;
        state.history = emptyHistory(); // structural changes aren't undoable
        state.persistedConfigs[tempId] = optimisticBlock.configuration;
      });

      // The parent may itself still be pending (e.g. a column added right after its section)
      const promise = (async () =>
        emailBuilderService.addBlock(document.id, {
          blockType,
          parentBlockId: await resolveOptionalBlockId(parentBlockId),
          orderIndex,
          configuration,
        }))();
      registerTempId(tempId, promise.then((block) => block.id));

      const operationId = `add-${tempId}`;
      set((state) => {
        state.pendingOperations.push({ id: operationId, type: "add", promise });
      });

      try {
        const realBlock = await promise;

        // Rename in place: keeps edits made to the block while the request was in flight,
        // and fixes children that were nested under the temporary ID
        set((state) => {
          const mapping = new Map([[tempId, realBlock.id]]);
          state.blockTree = renameBlockIds(state.blockTree, mapping);
          state.history = renameInHistory(state.history, mapping);
          state.persistedConfigs = renameKeys(state.persistedConfigs, mapping);
          if (state.selectedBlockId === tempId) {
            state.selectedBlockId = realBlock.id;
          }
          state.pendingOperations = state.pendingOperations.filter((op) => op.id !== operationId);
        });

        return realBlock;
      } catch (error: any) {
        get().handleOperationFailure(operationId, error.message || "Failed to add block");
        throw error;
      }
    },

    // Immediate local update without API call
    updateBlockConfigLocal: (blockId: string, configuration: any) => {
      const { blockTree } = get();
      
      const newTree = updateBlockConfiguration(
        blockTree,
        blockId,
        configuration
      );
      set((state) => {
        state.blockTree = newTree;
        state.isDirty = true;
      });
    },

    patchBlockConfigLocal: (blockId: string, patch: Record<string, unknown>) => {
      const block = findBlockById(get().blockTree, blockId);
      if (!block) return;
      get().updateBlockConfigLocal(blockId, { ...parseConfiguration(block.configuration), ...patch });
    },

    persistBlockConfig: async (blockId: string) => {
      const block = findBlockById(get().blockTree, blockId);
      if (!block) return; // deleted meanwhile
      await get().updateBlockConfig(blockId, parseConfiguration(block.configuration));
    },

    updateBlockConfig: async (blockId: string, configuration: any, options = {}) => {
      const { blockTree, document } = get();

      if (!document) {
        throw new Error("No document loaded");
      }

      // Find the block to get its blockType
      const block = findBlockById(blockTree, blockId);
      if (!block) {
        throw new Error(`Block ${blockId} not found`);
      }

      // Record the edit for undo: from what the server last had to what is being sent now
      const after = JSON.stringify(configuration);
      set((state) => {
        const before = state.persistedConfigs[blockId];
        if (options.recordHistory !== false && before !== undefined) {
          state.history = recordEdit(state.history, { blockId, before, after });
        }
        state.persistedConfigs[blockId] = after;
      });

      // Make API call with blockType for type discriminator
      const promise = (async () =>
        emailBuilderService.updateBlock(document.id, await resolveBlockId(blockId), block.blockType, configuration))();

      const operationId = `update-${blockId}-${Date.now()}`;
      set((state) => {
        state.pendingOperations.push({ id: operationId, type: "update", promise });
      });

      try {
        await promise;

        // The local tree already holds this (or a newer) configuration; overwriting it with the
        // server echo could revert edits typed while the request was in flight.
        set((state) => {
          state.pendingOperations = state.pendingOperations.filter((op) => op.id !== operationId);
        });
      } catch (error: any) {
        get().handleOperationFailure(operationId, error.message || "Failed to update block");
        throw error;
      }
    },

    moveBlockAction: async (
      blockId: string,
      parentBlockId: string | undefined,
      orderIndex: number
    ) => {
      const { blockTree, document } = get();

      if (!document) {
        throw new Error("No document loaded");
      }

      // Optimistically move block
      const newTree = moveBlockInTree(
        blockTree,
        blockId,
        parentBlockId ?? null,
        orderIndex
      );
      set((state) => {
        state.blockTree = newTree;
        state.isDirty = true;
        state.history = emptyHistory(); // structural changes aren't undoable
      });

      const promise = (async () =>
        emailBuilderService.moveBlock(
          document.id,
          await resolveBlockId(blockId),
          await resolveOptionalBlockId(parentBlockId),
          orderIndex
        ))();

      const operationId = `move-${blockId}-${Date.now()}`;
      set((state) => {
        state.pendingOperations.push({ id: operationId, type: "move", promise });
      });

      try {
        await promise;

        set((state) => {
          state.pendingOperations = state.pendingOperations.filter((op) => op.id !== operationId);
        });
      } catch (error: any) {
        get().handleOperationFailure(operationId, error.message || "Failed to move block");
        throw error;
      }
    },

    deleteBlockAction: async (blockId: string) => {
      const { blockTree, document } = get();

      if (!document) {
        throw new Error("No document loaded");
      }

      // Optimistically delete block
      const newTree = deleteBlockFromTree(blockTree, blockId);
      set((state) => {
        state.blockTree = newTree;
        state.isDirty = true;
        // Deselect if deleted block was selected
        if (state.selectedBlockId === blockId) {
          state.selectedBlockId = null;
        }
        state.history = emptyHistory(); // structural changes aren't undoable
      });

      const promise = (async () =>
        emailBuilderService.deleteBlock(document.id, await resolveBlockId(blockId)))();

      const operationId = `delete-${blockId}-${Date.now()}`;
      set((state) => {
        state.pendingOperations.push({ id: operationId, type: "delete", promise });
      });

      try {
        await promise;

        set((state) => {
          state.pendingOperations = state.pendingOperations.filter((op) => op.id !== operationId);
        });
      } catch (error: any) {
        get().handleOperationFailure(operationId, error.message || "Failed to delete block");
        throw error;
      }
    },

    duplicateBlockAction: async (blockId: string, targetParentId?: string) => {
      const { blockTree, document } = get();

      if (!document) {
        throw new Error("No document loaded");
      }

      // Optimistically duplicate block, with temporary IDs for the copy and all its children
      const { newTree, newBlockId } = duplicateBlockTree(blockTree, blockId, newTempId);
      const optimisticCopy = findBlockById(newTree, newBlockId);
      const tempIds = optimisticCopy ? preorderIds([optimisticCopy]) : [newBlockId];

      set((state) => {
        state.blockTree = newTree;
        state.isDirty = true;
        state.selectedBlockId = newBlockId;
        state.history = emptyHistory(); // structural changes aren't undoable
        if (optimisticCopy) Object.assign(state.persistedConfigs, collectConfigs([optimisticCopy]));
      });

      const promise = (async () =>
        emailBuilderService.duplicateBlock(
          document.id,
          await resolveBlockId(blockId),
          await resolveOptionalBlockId(targetParentId)
        ))();

      // The server copies the subtree in the same order, so positions in pre-order correspond
      const serverIds = promise.then((copy) => preorderIds([copy]));
      tempIds.forEach((tempId, index) =>
        registerTempId(
          tempId,
          serverIds.then((ids) => ids[index] ?? Promise.reject(new Error("Duplicated block not returned by server")))
        )
      );

      const operationId = `duplicate-${blockId}-${Date.now()}`;
      set((state) => {
        state.pendingOperations.push({ id: operationId, type: "duplicate", promise });
      });

      try {
        const ids = await serverIds;
        const mapping = new Map<string, string>();
        tempIds.forEach((tempId, index) => {
          if (ids[index]) mapping.set(tempId, ids[index]);
        });

        set((state) => {
          state.blockTree = renameBlockIds(state.blockTree, mapping);
          state.history = renameInHistory(state.history, mapping);
          state.persistedConfigs = renameKeys(state.persistedConfigs, mapping);
          if (state.selectedBlockId && mapping.has(state.selectedBlockId)) {
            state.selectedBlockId = mapping.get(state.selectedBlockId)!;
          }
          state.pendingOperations = state.pendingOperations.filter((op) => op.id !== operationId);
        });

        // The response nests children only a few levels deep; reload if part of the copy is unmapped
        if (mapping.size < tempIds.length) {
          void get().resyncBlockTree();
        }
      } catch (error: any) {
        get().handleOperationFailure(operationId, error.message || "Failed to duplicate block");
        throw error;
      }
    },

    handleOperationFailure: (operationId: string, message: string) => {
      set((state) => {
        state.error = message;
        state.pendingOperations = state.pendingOperations.filter((op) => op.id !== operationId);
      });
      // Restoring a snapshot taken when this request started would also undo every edit made
      // since (including ones still in flight); the server's state is the reliable fallback.
      void get().resyncBlockTree();
    },

    resyncBlockTree: async () => {
      const { document } = get();
      if (!document) return;

      // Let other in-flight requests land first so the reload includes them
      await Promise.allSettled(get().pendingOperations.map((op) => op.promise));

      try {
        const fresh = await emailBuilderService.getDocument(document.id);
        if (get().document?.id !== fresh.id) return; // navigated away meanwhile

        const tree = buildBlockTree(fresh.blocks || []);
        set((state) => {
          state.document = fresh;
          state.blockTree = tree;
          if (state.selectedBlockId && !findBlockById(tree, state.selectedBlockId)) {
            state.selectedBlockId = null;
          }
          // Local history may reference blocks that no longer exist
          state.history = emptyHistory();
          state.persistedConfigs = collectConfigs(tree);
        });
      } catch (error: any) {
        set((state) => {
          state.error = error.message || "Failed to reload the document; please refresh the page";
        });
      }
    },

    // ===== SELECTION ACTIONS =====

    selectBlock: (blockId: string | null) => {
      set((state) => {
        state.selectedBlockId = blockId;
      });
    },

    hoverBlock: (blockId: string | null) => {
      set((state) => {
        state.hoveredBlockId = blockId;
      });
    },

    // ===== UNDO/REDO ACTIONS =====

    undo: async () => {
      // A debounced edit still waiting is the most recent change; commit it so it is what's undone
      flushPendingEdits();
      const { history } = get();
      const edit = history.past[history.past.length - 1];
      if (!edit) return;

      set((state) => {
        state.history = { past: history.past.slice(0, -1), future: [edit, ...history.future] };
      });
      await applyHistoryConfig(edit.blockId, edit.before);
    },

    redo: async () => {
      flushPendingEdits();
      const { history } = get();
      const edit = history.future[0];
      if (!edit) return;

      set((state) => {
        state.history = { past: [...history.past, edit], future: history.future.slice(1) };
      });
      await applyHistoryConfig(edit.blockId, edit.after);
    },

    canUndo: () => get().history.past.length > 0,

    canRedo: () => get().history.future.length > 0,

    flushAndWaitForPendingOperations: async () => {
      flushPendingEdits();
      // Flushing can enqueue new operations, so wait until the queue is actually empty
      for (let round = 0; round < 10; round++) {
        const pending = get().pendingOperations.map((op) => op.promise);
        if (pending.length === 0) return;
        await Promise.allSettled(pending);
      }
    },

    // ===== VERSION ACTIONS =====

    createCheckpoint: async (description?: string, changeType: "Manual" | "AutoSave" = "Manual") => {
      const { document } = get();
      if (!document) return;

      try {
        // The version snapshots server state, so pending edits must land first
        await get().flushAndWaitForPendingOperations();
        await emailBuilderService.createVersion(document.id, description, changeType);
      } catch (error: any) {
        set((state) => {
          state.error = error.message || "Failed to create checkpoint";
        });
        throw error;
      }
    },

    restoreVersion: async (versionNumber: number) => {
      const { document } = get();
      if (!document) return;

      set((state) => {
        state.isLoading = true;
        state.error = null;
      });

      try {
        // Edits still waiting to be sent would otherwise land after the restore and overwrite it
        await get().flushAndWaitForPendingOperations();
        await emailBuilderService.restoreVersion(document.id, versionNumber);
        // The restore endpoint only returns a status message; load the restored document
        const restoredDocument = await emailBuilderService.getDocument(document.id);
        const tree = buildBlockTree(restoredDocument.blocks || []);

        set((state) => {
          state.document = restoredDocument;
          state.blockTree = tree;
          state.isLoading = false;
          state.isDirty = false;
          state.lastSaved = new Date();
          // Reset history after restore
          state.history = emptyHistory();
          state.persistedConfigs = collectConfigs(tree);
        });
      } catch (error: any) {
        set((state) => {
          state.isLoading = false;
          state.error = error.message || "Failed to restore version";
        });
        throw error;
      }
    },

    getVersionHistory: async () => {
      const { document } = get();
      if (!document) return;

      try {
        await emailBuilderService.getVersions(document.id);
        // Versions would typically be stored in document.versions
        // This is mainly for triggering a refetch
      } catch (error: any) {
        set((state) => {
          state.error = error.message || "Failed to get version history";
        });
        throw error;
      }
    },

    // ===== UTILITY ACTIONS =====

    reset: () => {
      clearTempIds();
      set((state) => {
        Object.assign(state, initialState);
      });
    },

    setError: (error: string | null) => {
      set((state) => {
        state.error = error;
      });
    },
  }))
);

// ===== HELPER FUNCTIONS =====

/**
 * Applies a configuration from the undo history locally and on the server, without recording it
 * as a new edit. A block deleted since then is skipped.
 */
async function applyHistoryConfig(blockId: string, configurationJson: string): Promise<void> {
  const store = useEmailBuilderStore.getState();
  if (!findBlockById(store.blockTree, blockId)) return;
  const configuration = parseConfiguration(configurationJson);
  store.updateBlockConfigLocal(blockId, configuration);
  try {
    await store.updateBlockConfig(blockId, configuration, { recordHistory: false });
  } catch {
    // handleOperationFailure already reported it and reloads the document
  }
}

function parseConfiguration(configuration: string | Record<string, unknown> | undefined): Record<string, unknown> {
  if (!configuration) return {};
  if (typeof configuration !== "string") return configuration;
  try {
    return JSON.parse(configuration);
  } catch {
    return {};
  }
}

/** Configuration JSON of every block in the tree, by ID. */
function collectConfigs(tree: EmailBlock[]): Record<string, string> {
  const configs: Record<string, string> = {};
  const visit = (blocks: EmailBlock[]) => {
    for (const block of blocks) {
      // Normalised, so an edit back to the original compares equal
      configs[block.id] = JSON.stringify(parseConfiguration(block.configuration));
      if (block.childBlocks?.length) visit(block.childBlocks);
    }
  };
  visit(tree);
  return configs;
}

/**
 * Add a block to the tree at the specified parent
 */
function addBlockToTree(
  tree: EmailBlock[],
  block: EmailBlock,
  parentId: string | undefined
): EmailBlock[] {
  if (!parentId) {
    // Add to root level at the specified orderIndex
    const newTree = [...tree];
    newTree.splice(block.orderIndex, 0, block);
    // Renumber orderIndex values to be sequential
    return newTree.map((node, index) => ({
      ...node,
      orderIndex: index,
    }));
  }

  // Add to parent's children
  return tree.map((node) => {
    if (node.id === parentId) {
      const newChildren = [...node.childBlocks];
      newChildren.splice(block.orderIndex, 0, block);
      // Renumber orderIndex values to be sequential
      const renumberedChildren = newChildren.map((child, index) => ({
        ...child,
        orderIndex: index,
      }));
      return {
        ...node,
        childBlocks: renumberedChildren,
      };
    }
    if (node.childBlocks.length > 0) {
      return {
        ...node,
        childBlocks: addBlockToTree(node.childBlocks, block, parentId),
      };
    }
    return node;
  });
}

/**
 * Update block configuration in tree
 */
function updateBlockConfiguration(
  tree: EmailBlock[],
  blockId: string,
  configuration: any
): EmailBlock[] {
  // Only the path to the changed block gets new objects, so memoized renderers of every other
  // block can skip re-rendering
  let changed = false;
  const result = tree.map((node) => {
    if (node.id === blockId) {
      changed = true;
      return {
        ...node,
        configuration: JSON.stringify(configuration),
        updatedAt: new Date().toISOString(),
      };
    }
    if (node.childBlocks.length > 0) {
      const childBlocks = updateBlockConfiguration(node.childBlocks, blockId, configuration);
      if (childBlocks !== node.childBlocks) {
        changed = true;
        return { ...node, childBlocks };
      }
    }
    return node;
  });
  return changed ? result : tree;
}

